// Admin Payout Holds
// ─────────────────────────────────────────────────────────────
// The operator's view of tax-gated payouts: which stores are being
// chased, which have money sitting on the platform balance, and how
// much of other people's money FASH is currently holding.
//
// Everything here comes from callables — the client cannot query the
// payout ledger across businesses.

import { useState, useEffect, useCallback } from 'react';
import {
  AlertTriangle,
  Clock,
  Loader2,
  RefreshCcw,
  Send,
  ShieldCheck,
  Wallet,
} from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../config/firebase';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { cn } from '../../lib/utils';
import type { MissingTaxRequirement } from '../../types/business';

interface HoldRow {
  businessId: string;
  businessName: string;
  ownerId: string;
  count: number;
  totalCents: number;
  oldestHeldAtMs: number | null;
  holdReasons: string[];
  missing: MissingTaxRequirement[];
  stripeBlockers: string[];
  payoutEligible: boolean;
  inGracePeriod: boolean;
  graceEndsAtMs: number | null;
  lastNoticeAtMs: number | null;
  noticeCount: number;
}

interface HoldsResponse {
  totals: { count: number; totalCents: number; businessCount: number };
  businesses: HoldRow[];
}

interface BackfillResult {
  dryRun: boolean;
  evaluated: number;
  compliant: number;
  nonCompliant: number;
  notified: number;
  failures: Array<{ businessId: string; error: string }>;
}

function money(cents: number): string {
  return `$${(cents / 100).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function relativeDays(ms: number | null): string {
  if (!ms) return '—';
  const days = Math.round((Date.now() - ms) / 86_400_000);
  if (days === 0) return 'today';
  if (days > 0) return `${days}d ago`;
  return `in ${Math.abs(days)}d`;
}

export function AdminPayoutHolds() {
  const [data, setData] = useState<HoldsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const call = httpsCallable<Record<string, never>, HoldsResponse>(
        functions,
        'adminListPayoutHolds',
      );
      const { data: result } = await call({});
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load payout holds');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const release = async (row: HoldRow, force: boolean) => {
    // A forced release pays a store that has not satisfied the gate, so it
    // is recorded against the admin who authorised it.
    let reason = '';
    if (force) {
      const input = window.prompt(
        `Override the compliance hold for ${row.businessName} and release ${money(row.totalCents)}?\n\n` +
        'This pays a store whose tax information is still incomplete. ' +
        'Enter a reason for the audit log:',
      );
      if (!input?.trim()) return;
      reason = input.trim();
    }

    setBusyId(row.businessId);
    try {
      const call = httpsCallable<
        { businessId: string; force: boolean; reason?: string },
        { released: number; totalCents: number; failed: number; skipped: string | null }
      >(functions, 'adminReleasePayouts');
      const { data: result } = await call({ businessId: row.businessId, force, reason });

      if (result.skipped) {
        setError(
          result.skipped === 'not_eligible'
            ? `${row.businessName} is still not compliant. Use Override to pay anyway.`
            : `Nothing released: ${result.skipped.replace(/_/g, ' ')}.`,
        );
      }
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Release failed');
    } finally {
      setBusyId(null);
    }
  };

  const remind = async (row: HoldRow) => {
    setBusyId(row.businessId);
    try {
      const call = httpsCallable<{ businessId: string }, unknown>(
        functions,
        'adminRefreshTaxCompliance',
      );
      await call({ businessId: row.businessId });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Refresh failed');
    } finally {
      setBusyId(null);
    }
  };

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  const rows = data?.businesses ?? [];
  const holding = rows.filter((r) => r.totalCents > 0);
  const atRisk = rows.filter((r) => r.totalCents === 0);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-heading">Payout Holds</h1>
          <p className="mt-1 text-sm text-body">
            Stores whose proceeds FASH is withholding pending taxpayer information,
            and those approaching their deadline.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => void load()} disabled={loading}>
          <RefreshCcw className={cn('mr-2 h-4 w-4', loading && 'animate-spin')} />
          Refresh
        </Button>
      </div>

      <RolloutPanel onDone={() => void load()} />

      {error && (
        <div className="flex items-start gap-3 rounded-2xl border border-red-200 bg-red-50 p-4">
          <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-600" />
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {/* Platform-level totals — this is other people's money on our balance */}
      <div className="grid gap-4 sm:grid-cols-3">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-body">
              <Wallet className="h-3.5 w-3.5" />
              Total held
            </div>
            <p className="mt-1 text-2xl font-semibold text-heading">
              {money(data?.totals.totalCents ?? 0)}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-xs uppercase tracking-wide text-body">Held orders</div>
            <p className="mt-1 text-2xl font-semibold text-heading">
              {data?.totals.count ?? 0}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-xs uppercase tracking-wide text-body">Stores affected</div>
            <p className="mt-1 text-2xl font-semibold text-heading">
              {data?.totals.businessCount ?? 0}
            </p>
          </CardContent>
        </Card>
      </div>

      <HoldSection
        title="Money on hold"
        empty="No store currently has held funds."
        rows={holding}
        busyId={busyId}
        expanded={expanded}
        onToggle={setExpanded}
        onRelease={release}
        onRefresh={remind}
      />

      <HoldSection
        title="At risk — nothing held yet"
        empty="Every active store has complete tax information."
        rows={atRisk}
        busyId={busyId}
        expanded={expanded}
        onToggle={setExpanded}
        onRelease={release}
        onRefresh={remind}
      />
    </div>
  );
}

function HoldSection({
  title,
  empty,
  rows,
  busyId,
  expanded,
  onToggle,
  onRelease,
  onRefresh,
}: {
  title: string;
  empty: string;
  rows: HoldRow[];
  busyId: string | null;
  expanded: string | null;
  onToggle: (id: string | null) => void;
  onRelease: (row: HoldRow, force: boolean) => void;
  onRefresh: (row: HoldRow) => void;
}) {
  return (
    <div className="space-y-2">
      <h2 className="text-sm font-semibold text-heading">
        {title} <span className="font-normal text-body">({rows.length})</span>
      </h2>

      {rows.length === 0 ? (
        <Card>
          <CardContent className="p-6 text-center text-sm text-body">{empty}</CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {rows.map((row) => {
            const isOpen = expanded === row.businessId;
            const busy = busyId === row.businessId;
            const blockers = [...row.missing.map((m) => m.label), ...row.stripeBlockers];

            return (
              <Card key={row.businessId}>
                <CardContent className="p-4">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <button
                      type="button"
                      onClick={() => onToggle(isOpen ? null : row.businessId)}
                      className="min-w-0 flex-1 text-left"
                    >
                      <div className="flex items-center gap-2">
                        <span className="truncate text-sm font-semibold text-heading">
                          {row.businessName}
                        </span>
                        {row.payoutEligible ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-[11px] font-medium text-green-800">
                            <ShieldCheck className="h-3 w-3" /> Now eligible
                          </span>
                        ) : row.inGracePeriod ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-800">
                            <Clock className="h-3 w-3" />
                            Due {relativeDays(row.graceEndsAtMs)}
                          </span>
                        ) : (
                          <span className="rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-medium text-red-800">
                            Holding
                          </span>
                        )}
                      </div>
                      <p className="mt-0.5 text-xs text-body">
                        {row.count > 0
                          ? `${money(row.totalCents)} across ${row.count} order${row.count === 1 ? '' : 's'} · oldest ${relativeDays(row.oldestHeldAtMs)}`
                          : `${blockers.length} requirement${blockers.length === 1 ? '' : 's'} outstanding`}
                        {row.noticeCount > 0 &&
                          ` · ${row.noticeCount} notice${row.noticeCount === 1 ? '' : 's'} sent, last ${relativeDays(row.lastNoticeAtMs)}`}
                      </p>
                    </button>

                    <div className="flex flex-shrink-0 gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onRefresh(row)}
                        disabled={busy}
                      >
                        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Re-check'}
                      </Button>
                      {row.count > 0 && (
                        <>
                          <Button
                            size="sm"
                            onClick={() => onRelease(row, false)}
                            disabled={busy || !row.payoutEligible}
                          >
                            <Send className="mr-1.5 h-3.5 w-3.5" />
                            Release
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => onRelease(row, true)}
                            disabled={busy}
                          >
                            Override
                          </Button>
                        </>
                      )}
                    </div>
                  </div>

                  {isOpen && (
                    <div className="mt-3 border-t border-gray-100 pt-3">
                      <p className="text-xs font-medium text-heading">Outstanding</p>
                      {blockers.length === 0 ? (
                        <p className="mt-1 text-xs text-body">
                          Nothing outstanding — re-check to release.
                        </p>
                      ) : (
                        <ul className="mt-1.5 space-y-1">
                          {blockers.map((label) => (
                            <li key={label} className="flex items-start gap-2 text-xs text-body">
                              <span
                                aria-hidden
                                className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-gray-400"
                              />
                              <span>{label}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                      <p className="mt-3 font-mono text-[11px] text-gray-400">
                        {row.businessId}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

/**
 * The one-time rollout control. Evaluating every store starts its 30-day
 * clock and sends the opening notice, so it previews first and the real run
 * has to be confirmed against the number the preview reported.
 */
function RolloutPanel({ onDone }: { onDone: () => void }) {
  const [preview, setPreview] = useState<BackfillResult | null>(null);
  const [running, setRunning] = useState(false);
  const [done, setDone] = useState<BackfillResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const run = async (dryRun: boolean) => {
    setRunning(true);
    setError(null);
    try {
      const call = httpsCallable<{ dryRun: boolean; notify: boolean }, BackfillResult>(
        functions,
        'adminBackfillTaxCompliance',
      );
      const { data } = await call({ dryRun, notify: true });
      if (dryRun) {
        setPreview(data);
      } else {
        setDone(data);
        onDone();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Backfill failed');
    } finally {
      setRunning(false);
    }
  };

  if (done) {
    return (
      <Card>
        <CardContent className="p-4">
          <p className="text-sm font-semibold text-heading">Rollout complete</p>
          <p className="mt-1 text-sm text-body">
            Evaluated {done.evaluated} store{done.evaluated === 1 ? '' : 's'}:{' '}
            {done.compliant} already compliant, {done.nonCompliant} not. Sent{' '}
            {done.notified} opening notice{done.notified === 1 ? '' : 's'}.
            {done.failures.length > 0 && ` ${done.failures.length} failed.`}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="p-4">
        <p className="text-sm font-semibold text-heading">Tax compliance rollout</p>
        <p className="mt-1 text-sm text-body">
          Evaluates every store, starts the 30-day countdown for those that are not
          compliant, and emails them what is missing. Nothing is withheld until a
          store&apos;s own deadline passes. Run this once.
        </p>

        {error && <p className="mt-2 text-sm text-red-700">{error}</p>}

        {preview && (
          <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 p-3">
            <p className="text-sm text-amber-900">
              <span className="font-semibold">{preview.nonCompliant}</span> of{' '}
              {preview.evaluated} stores are not payout-eligible. Running for real starts
              their clock and emails each one.
            </p>
          </div>
        )}

        <div className="mt-3 flex gap-2">
          <Button variant="outline" size="sm" onClick={() => void run(true)} disabled={running}>
            {running && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />}
            Preview
          </Button>
          <Button
            size="sm"
            disabled={running || !preview}
            onClick={() => {
              if (
                window.confirm(
                  `Start the compliance clock for ${preview?.nonCompliant} store(s) and email them now?`,
                )
              ) {
                void run(false);
              }
            }}
          >
            Run rollout
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
